import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import CopyButton from '../components/docs/CopyButton'
import Icon from '../components/docs/Icon'
import Flag from '../components/marks/Flag'
import { usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'

const cssImport = `@import url('/mds/tokens.css');
@import url('/mds/fonts.css');
@import url('/mds/themes.css');`

const sections = [
  {
    to: '/foundations/color',
    title: 'Color',
    text: 'Semantic roles in light and dark, the American palette and a verified theme for every mission.',
    art: (
      <span className="fnd-card__art fnd-card__art--color" aria-hidden="true">
        <span style={{ background: 'var(--mds-primary)' }} />
        <span style={{ background: 'var(--mds-accent)' }} />
        <span style={{ background: 'var(--mds-ink)' }} />
        <span style={{ background: 'var(--mds-surface)' }} />
      </span>
    ),
  },
  {
    to: '/foundations/typography',
    title: 'Typography',
    text: 'A display serif with a voice, a precise sans for everything else and open-license alternates.',
    art: (
      <span className="fnd-card__art fnd-card__art--type" aria-hidden="true">
        Aa
      </span>
    ),
  },
  {
    to: '/foundations/layout',
    title: 'Layout',
    text: 'A four-point spacing scale, two densities, one centered column and even grids that reflow at 320px.',
    art: (
      <span className="fnd-card__art fnd-card__art--layout" aria-hidden="true">
        {[1, 2, 3, 4, 5, 6].map((n) => (
          <span key={n} />
        ))}
      </span>
    ),
  },
  {
    to: '/foundations/shape',
    title: 'Shape',
    text: 'Corner radius in three styles, borders, the focus ring, surfaces and elevation.',
    art: (
      <span className="fnd-card__art fnd-card__art--space" aria-hidden="true">
        {[1, 2, 3, 4].map((n) => (
          <span key={n} style={{ width: `${n * 18}%` }} />
        ))}
      </span>
    ),
  },
  {
    to: '/foundations/motion',
    title: 'Motion',
    text: 'Three easing curves, four durations and a full stop for visitors who ask for reduced motion.',
    art: (
      <span className="fnd-card__art fnd-card__art--motion" aria-hidden="true">
        <svg viewBox="0 0 100 60" fill="none">
          <path d="M4 56C26 56 30 4 96 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </span>
    ),
  },
]

export default function FoundationsPage() {
  const { prefs } = usePrefs()
  return (
    <DocLayout
      eyebrow="Foundations"
      title="A common language."
      intro="Color, type, space, layout, motion and identity: the decisions every MDS site shares, published as tokens a designer, an engineer and an agent read the same way."
      wide
    >
      <section className="doc-section" aria-labelledby="fnd-sections">
        <h2 id="fnd-sections" className="mds-visually-hidden">
          <Cased>Foundation topics</Cased>
        </h2>
        <div className="fnd-cards">
          {sections.map((section) => (
            <article key={section.to} className="mds-card mds-card--outline mds-card--link fnd-card">
              {section.art}
              <h3 className="mds-card__title">
                <Link to={section.to} className="mds-card__link">
                  {section.title}
                </Link>
              </h3>
              <p className="mds-muted">{section.text}</p>
            </article>
          ))}
          <article className="mds-card mds-card--outline mds-card--link fnd-card fnd-card--identity">
            <span className="fnd-card__art fnd-card__art--identity" aria-hidden="true">
              <Flag variant={prefs.mark} />
            </span>
            <h3 className="mds-card__title">
              <Link to="/foundations/identity" className="mds-card__link">
                Identity
              </Link>
            </h3>
            <p className="mds-muted">Flags, seals and emblems in five styles, the banner and the identifier.</p>
          </article>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="fnd-import">
        <h2 id="fnd-import"><Cased>Use the tokens</Cased></h2>
        <p>
          The foundations ship as three stylesheets. Tokens set the American theme and the core scales, fonts switch
          typefaces by attribute, and themes add every mission. Put <code>data-theme</code> and{' '}
          <code>data-mission</code> on the root element to choose a mode and a mission.
        </p>
        <pre className="mds-code" tabIndex={0}>
          <code>{cssImport}</code>
        </pre>
        <div className="mds-cluster">
          <CopyButton text={cssImport} label="Copy CSS import" />
          <a href="/mds/tokens.css" download className="mds-button mds-button--secondary mds-button--sm">
            <Icon name="download" size={16} /> tokens.css
          </a>
          <a href="/mds/tokens.json" className="mds-link">
            Tokens as JSON
          </a>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="fnd-principles">
        <h2 id="fnd-principles"><Cased>Principles</Cased></h2>
        <div className="fnd-principles">
          {[
            ['Roles, not colors', 'Components read semantic roles such as primary and ink-muted, so a mission theme or dark mode changes every screen at once.'],
            ['Contrast is computed', 'Every role pair meets WCAG 2.1 AA for every mission, in light and dark.'],
            ['Open and self-hosted', 'Every typeface is open-license from Google Fonts and ships with the site, with no request to a font service.'],
            ['Calm by default', 'Motion explains change and stops entirely when a visitor asks for reduced motion.'],
          ].map(([title, text]) => (
            <div key={title} className="fnd-principle">
              <h3><Cased>{title}</Cased></h3>
              <p className="mds-muted">{text}</p>
            </div>
          ))}
        </div>
      </section>
    </DocLayout>
  )
}
